import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { dismissToastAtIndex, dismissExpiredToasts } from "./lib/toastGuard";
import { scrollWindowToTop } from "./lib/scrollTop";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Schedule from "./pages/Schedule";
import Invoices from "./pages/Invoices";
import Packages from "./pages/Packages";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import DataDeletion from "./pages/DataDeletion";
import TrialExpired from "./pages/TrialExpired";
import PwaInstallPrompt from "./components/PwaInstallPrompt";

const TOAST_OPTIONS = {
  style: { background: "#FFFFFF", color: "#2A2623", border: "1px solid #E8E1D6" },
};

function AppToaster() {
  const { user } = useAuth();
  // While impersonating, push toasts below the fixed green banner so they don't overlap the Exit button.
  const offset = user?.impersonating ? 56 : 16;
  // On a phone the toasts sit at the bottom. The breakpoint matches sonner's
  // own mobile cutoff (600px) so the position switch and the mobile offset
  // below always agree.
  const [isPhone, setIsPhone] = React.useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 600px)").matches : false,
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 600px)");
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // The browser throttles sonner's auto-dismiss timer while the tab is hidden,
  // so a toast can still be on screen when the user returns from another app.
  // On every way back to the foreground, purge any toast whose time is up.
  React.useEffect(() => {
    const onResume = () => {
      if (document.visibilityState === "visible") dismissExpiredToasts();
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("pageshow", onResume);
    window.addEventListener("focus", onResume);
    return () => {
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("pageshow", onResume);
      window.removeEventListener("focus", onResume);
    };
  }, []);

  // A Radix Dialog/Sheet is modal: it sets `pointer-events: none` on <body> and
  // re-enables them only inside its own subtree. The toast layer lives outside
  // that subtree, so a tap on a toast used to fall straight through to whatever
  // sat behind it (see index.css for the opt-back-in). stopPropagation keeps
  // that tap from reaching Radix's document-level dismiss handler, so the
  // drawer or dialog underneath stays open while the toast action runs.
  const keepInside = (e) => e.stopPropagation();
  // Tapping anywhere on a toast closes it — except the Undo / action button,
  // which must run instead. Swipe-to-dismiss is handled natively by sonner.
  const handleTap = (e) => {
    e.stopPropagation();
    if (e.target.closest("[data-button]") || e.target.closest("[data-close-button]")) return;
    const li = e.target.closest("[data-sonner-toast]");
    if (!li) return;
    dismissToastAtIndex(Number(li.getAttribute("data-index")));
  };
  return (
    <div
      data-testid="toast-layer"
      onPointerDown={keepInside}
      onMouseDown={keepInside}
      onTouchStart={keepInside}
      onClick={handleTap}
    >
      <Toaster
        position={isPhone ? "bottom-center" : "top-right"}
        theme="dark"
        offset={offset}
        mobileOffset={{
          bottom: "calc(64px + env(safe-area-inset-bottom))",
          left: "16px",
          right: "16px",
        }}
        visibleToasts={2}
        swipeDirections={isPhone ? ["bottom", "left", "right"] : ["top", "right"]}
        toastOptions={TOAST_OPTIONS}
      />
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useLayoutEffect(() => {
    if (hash) return; // leave #anchor links to the browser
    // `html` carries scroll-behavior:smooth, so a plain scrollTo(0,0) animates —
    // and Safari drops that animation when the route swap resizes the document,
    // leaving the new page parked where the old one was. Force an instant jump.
    scrollWindowToTop();
  }, [pathname, hash]);
  return null;
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="root-loading">
        <div className="w-8 h-8 border-2 border-[#0F6B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin" element={<Admin />} />
          </Route>
          <Route
            path="/trial-expired"
            element={
              <ProtectedRoute>
                <TrialExpired />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <AppToaster />
      <PwaInstallPrompt />
    </AuthProvider>
  );
}

export default App;
